import { isArray } from 'lodash/fp'
import * as React from 'react'
import { OnChangeValue } from 'react-select'
import Select from '../../components/Select'
import DebouncedTextField from '../../components/DebouncedTextField'
import Loader from '../../components/Loader'

import { FilterProps } from '../../interfaces/pages/projects'
import { ProjectStatus } from '../../interfaces/entities/project'
import { Client } from '../../interfaces/entities/client'

import DI from '../../app/di'
import { UserGroups } from '../../interfaces/services/user'
import { translateWithUserLocale } from '../../utils/i18n'

import {
  Box,
  InputLabel,
  InputAdornment,
  Icon,
  Paper,
  Divider,
  styled,
} from '@design/pt-ui'
const FilterColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  marginRight: theme.spacing(3),
  width: '30%',
  textAlign: 'left',
}))

interface IProjectsFilterProps extends FilterProps {
  userGroups: UserGroups
}
interface IProjectsFilterState {
  clients: Client[] | null
  statuses: ProjectStatus[] | null
}

function ProjectsFilterComponent(props: IProjectsFilterProps) {
  const [state, setState] = React.useState<IProjectsFilterState>({
    clients: null,
    statuses: null,
  })

  const getProjectService = () => {
    return DI.get('ProjectService')
  }

  React.useEffect(() => {
    const projectService = getProjectService()
    Promise.all([
      projectService.getClients(),
      projectService.getProjectStatuses(),
    ]).then(([clients, statuses]) => {
      if (statuses.type === 'error' || clients.type === 'error') {
        console.warn('Error loading clients and/or project status')
      }

      if (statuses.type === 'success' && clients.type === 'success') {
        setState((state) => ({
          ...state,
          clients: clients.payload,
          statuses: statuses.payload,
        }))
      }
    })
  }, [])

  const handleStatusChange = (
    value: OnChangeValue<{ value: string; label: string }, boolean>,
  ) => {
    if (isArray(value)) {
      const ids = value.map((i) => i.value)

      const statuses = (state.statuses as ProjectStatus[]).filter((s) => {
        return ids.findIndex((id) => id === s.name) !== -1
      })
      props.onFilterChange({
        ...props.filter,
        statuses,
      })
    }
  }

  const handleClientChange = (
    value: OnChangeValue<{ value: string; label: string }, boolean>,
  ) => {
    if (isArray(value)) {
      const ids = value.map((i) => i.value)

      const clients =
        (state.clients as Client[]).filter((c) => {
          return ids.findIndex((id) => id === c.id) !== -1
        }) || null
      props.onFilterChange({
        ...props.filter,
        clients,
      })
    }
  }

  const handleSearch = (query: string) => {
    props.onFilterChange({
      ...props.filter,
      query,
    })
  }

  if (state.clients === null || state.statuses === null) {
    return (
      <Loader
        variant="flex"
        message={translateWithUserLocale('loaders.loading_filter')}
      />
    )
  }

  const clients = state.clients.map((client) => {
    return {
      value: client.id,
      label: client.name,
    }
  })

  const statuses = state.statuses.map((status) => {
    return {
      value: status.name,
      label: status.title,
    }
  })

  return (
    <div>
      <Paper sx={{ padding: 0.5, textAlign: 'left' }}>
        <Box sx={{ paddingLeft: 2 }}>
          <h3>{translateWithUserLocale('filter')}</h3>
        </Box>
        <Divider component="h3" />
        <Box
          sx={{
            display: 'flex',
            flexFlow: 'row',
            padding: 3,
            alignItems: 'flex-end',
          }}>
          {props.userGroups.includes('client') ? null : (
            <FilterColumn>
              <InputLabel>{translateWithUserLocale('client')}</InputLabel>
              <Select
                isMulti={true}
                onChange={handleClientChange}
                value={props.filter.clients.map((client) => ({
                  value: client.id,
                  label: client.name,
                }))}
                options={clients}
                placeholder={translateWithUserLocale('projects.select_client')}
              />
            </FilterColumn>
          )}
          <FilterColumn>
            <InputLabel>{translateWithUserLocale('status')}</InputLabel>
            <Select
              isMulti={true}
              onChange={handleStatusChange}
              value={props.filter.statuses.map((status) => ({
                value: status.name,
                label: status.title,
              }))}
              options={statuses}
              placeholder={translateWithUserLocale('projects.select_status')}
            />
          </FilterColumn>
          <Box sx={{ width: '30%', marginBottom: 0 }}>
            <DebouncedTextField
              value={props.filter.query}
              onChange={handleSearch}
              text={{
                type: 'search',
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>search</Icon>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        </Box>
      </Paper>
    </div>
  )
}

export default ProjectsFilterComponent
